import { Backdrop, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePost } from "src/hooks/usePost";
import { setHtmlLang } from "src/utils/setHtmlLang";
import Button from "../components/common/Button";
import InputField from "../components/common/InputField";

interface Props {
  lang?: string;
}

const ResetPasswordPage: VFC<Props> = ({ lang = "en" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [t, i18n] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const code = searchParams.get("code");

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
      setHtmlLang(lang);
    } else {
      i18n.changeLanguage("en");
      setHtmlLang("en");
    }
  }, [i18n, lang]);

  useEffect(() => {
    if (!code) {
      enqueueSnackbar(t("sign_in.password_reset.invalid_code"), {
        variant: "error",
      });
      navigate("/signin");
    }
  }, [code, navigate, enqueueSnackbar, t]);

  useEffect(() => {
    if (password !== "" && password.length < 8) {
      setErrorMessage(t("modal.change_password.error.length"));
      return;
    }

    if (
      password !== "" &&
      confirmPassword !== "" &&
      password !== confirmPassword
    ) {
      setErrorMessage(t("modal.change_password.error.match"));
    } else {
      setErrorMessage("");
    }
  }, [password, confirmPassword, t]);

  const { post: postResetPassword } = usePost({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/auth/reset-password`,
    params: {
      code: code || "",
      password: password,
    },
    onSuccess: () => {
      enqueueSnackbar(t("sign_in.reset_password"), {
        variant: "success",
      });
      setIsLoading(false);
      navigate("/signin");
    },
    onError: () => {
      enqueueSnackbar(t("error.login_again"), {
        variant: "error",
      });
      setIsLoading(false);
    },
  });

  const onChangePassword = (value: string) => {
    setPassword(value);
  };

  const onChangeConfirmPassword = (value: string) => {
    setConfirmPassword(value);
  };

  const handleSubmit = async () => {
    if (
      !code ||
      !password ||
      password !== confirmPassword ||
      password.length < 8
    ) {
      return;
    }

    setIsLoading(true);
    await postResetPassword({});
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== "Enter") return;
    handleSubmit();
  };

  const isSubmitDisabled =
    !code ||
    !password ||
    !confirmPassword ||
    password !== confirmPassword ||
    password.length < 8 ||
    isLoading;

  return (
    <main className="flex-grow flex justify-center items-center">
      <Backdrop open={isLoading} style={{ zIndex: 10001 }}>
        <CircularProgress />
      </Backdrop>
      <div className="shadow-md max-w-sp w-96 p-8 bg-white rounded-2xl">
        <div className="flex justify-center w-full text-2xl font-bold mb-6">
          {t("sign_in.password_reset.title")}
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <InputField
              label={t("sidemenu.reset_password.new_password")}
              type="password"
              value={password}
              onChange={onChangePassword}
              placeholder={t(
                "sidemenu.reset_password.new_password.placeholder"
              )}
            />
          </div>
          <div>
            <InputField
              label={t("sidemenu.reset_password.confirmation_password")}
              type="password"
              value={confirmPassword}
              onChange={onChangeConfirmPassword}
              placeholder={t(
                "sidemenu.reset_password.confirmation_password.placeholder"
              )}
              onKeyDown={handleKeyDown}
            />
          </div>
          {errorMessage && (
            <div className="text-base text-red-400">{errorMessage}</div>
          )}
          <div>
            <Button
              onClick={handleSubmit}
              text={t("general.submit")}
              isFullWidth
              disabled={isSubmitDisabled}
            />
          </div>
          <div className="flex justify-center mt-4">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => navigate("/signin")}
            >
              {t("sign_in.back_to_signin")}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
